import React from 'react';
import { View, ScrollView } from 'react-native';
import { scale as initialScale } from '@components/frame';
import { font as initialFont } from '@styles/fonts';
import { SCREEN_BORDER_RADIUS, TEST_IDS } from '@v2/pages/common/constants/base.constants';
import { isPhone } from '@helpers';
import NcFileContent from '@components/common/NCFile';

import WizardHeader from './components/WizardHeader';
import WizardProgressBar from './components/WizardProgressBar';
import StepTypeSelection from './components/StepTypeSelection';
import StepLinkToPart from './components/StepLinkToPart';
import StepTime from './components/StepTime';
import StepSummary from './components/StepSummary';
import useManageNCFileWizardNoERP from './hooks/useManageNCFileWizardNoERP';
import { NCFileWizardNoERPProps } from './types';

const NCFileWizardNoERP: React.FC<NCFileWizardNoERPProps> = ({
  C,
  ncFileId,
  ncFileName,
  hide,
  productionBatchId,
  selectedItem,
  scale = initialScale,
  font = initialFont,
}) => {
  const {
    currentStep,
    selectedType,
    setSelectedType,
    partMode,
    setPartMode,
    partData,
    setPartData,
    timeData,
    setTimeData,
    skippedSteps,
    loading,
    ncFileData,
    searchQuery,
    setSearchQuery,
    searchResults,
    searchLoading,
    handleSearch,
    searchPartsForAutocomplete,
    canSkip,
    handleNext,
    handleSkip,
    handleBack,
    handleStepPress,
  } = useManageNCFileWizardNoERP({ ncFileId, ncFileName, hide, productionBatchId });

  const resolvedNcFileName = ncFileData?.ncFileName || ncFileName;

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <StepTypeSelection selectedType={selectedType} onSelectType={setSelectedType} loading={loading} C={C} scale={scale} font={font} />;
      case 2:
        return (
          <StepLinkToPart
            partMode={partMode}
            setPartMode={setPartMode}
            partData={partData}
            setPartData={setPartData}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            searchResults={searchResults}
            searchLoading={searchLoading}
            onSearch={handleSearch}
            onPartsAutocomplete={searchPartsForAutocomplete}
            C={C}
            scale={scale}
            font={font}
          />
        );
      case 3:
        return <StepTime timeData={timeData} setTimeData={setTimeData} C={C} scale={scale} font={font} />;
      case 4:
        return (
          <StepSummary
            ncFileName={resolvedNcFileName}
            selectedType={selectedType}
            partData={partData}
            timeData={timeData}
            skippedSteps={skippedSteps}
            C={C}
            scale={scale}
            font={font}
          />
        );
      default:
        return null;
    }
  };

  return (
    <View testID={TEST_IDS.popups.linkNcFileWithoutERP} style={{ flex: 1, backgroundColor: C('Background/Light/Panel') }}>
      <WizardHeader
        currentStep={currentStep}
        selectedType={selectedType}
        ncFileName={resolvedNcFileName}
        loading={loading}
        canSkip={canSkip}
        onBack={handleBack}
        onNext={handleNext}
        onSkip={handleSkip}
        C={C}
        scale={scale}
        font={font}
      />

      <WizardProgressBar currentStep={currentStep} selectedType={selectedType} skippedSteps={skippedSteps} onStepPress={handleStepPress} C={C} scale={scale} font={font} />

      <View style={{ flex: 1, flexDirection: isPhone ? 'column' : 'row', padding: scale(15) }}>
        {/* Left column: NC File Content (hidden on phone) */}
        {!isPhone && (
          <View
            style={{
              width: '40%',
              marginRight: scale(15),
              backgroundColor: C('Background/Light/Field lighter'),
              borderRadius: scale(SCREEN_BORDER_RADIUS),
              overflow: 'hidden',
            }}
          >
            <NcFileContent
              selectedItem={selectedItem}
              C={C}
              font={font}
              backgroundColor={C('Background/Light/Field lighter')}
              ncFileId={ncFileId}
              ncFileName={resolvedNcFileName}
              showNcFileButtonCallBackOnPress={hide}
              setIsNcFileContentAvailable={() => {}}
            />
          </View>
        )}

        {/* Right column: Wizard step content (full width on phone) */}
        <ScrollView style={{ flex: 1 }} contentContainerStyle={{ flexGrow: 0 }}>
          <View
            style={{
              backgroundColor: C('Background/Light/Field lighter'),
              borderRadius: scale(SCREEN_BORDER_RADIUS),
              paddingVertical: scale(15),
            }}
          >
            {renderStep()}
          </View>
        </ScrollView>
      </View>
    </View>
  );
};

export default NCFileWizardNoERP;
